import React, {useEffect, useState, useCallback, useRef, useMemo} from 'react';
import {useNavigate} from "react-router-dom";
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import {
    DataGrid,
    GridFilterModel, GridSortModel, GridLinkOperator, GridRowId, GridColDef, GridRenderCellParams
} from "@mui/x-data-grid";
import {useAuth} from "../../_auth/useAuth";
import {
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings, ListViewToolbar,
    DialogBox, IDialogTextProps,
    MessageBox, MsgBoxType, IMessagePanelProps,
    MuiGridStateProp, MuiGridDataProp, muiGridFilterIsValid, FormTitle
} from "../_shared";
import {IItemIdsRequest, IMuiDataRequest, licenseeApi} from "../../_api";
import {AuthRolesEnum, licenseeViewColumns} from "../../_appData";
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';

export const LicenseeListView: React.FC = () => {
    const { isAuthenticated, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();

    // Create columns with Actions column and view icon (navigate with state for view mode)
    const columns = useMemo(() => {
        const actionsColumn: GridColDef = {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/licensee/${params.row.licensee_id}`, {
                                state: { mode: 'view' }
                            });
                        }}
                        sx={{
                            padding: '4px',
                            color: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                );
            },
        };
        return [actionsColumn, ...licenseeViewColumns];
    }, [navigate]);
    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
    const getGridDataRef = useRef<() => Promise<void>>();
    const [dlgBoxOpen, setDlgBoxOpen] = React.useState<boolean>(false);
    const [dlgBoxProps, setDlgBoxProps] = useState<IDialogTextProps>({message: ""});
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({message: ""});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [gridState, setGridState] = useState<MuiGridStateProp>({
        page: 0,
        page_size: DaGridSettings.defaultPageSize,
        filters: {
            items: [],
            linkOperator: GridLinkOperator.Or
        },
        sorts: [],
    });
    const [gridData, setGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const deleteSelectedItems = useCallback(async (row_ids: Array<string | number>) => {
        setIsLoading(true);

        const options: IItemIdsRequest = {item_ids: row_ids};
        const auth_token: string = await getAccessToken();

        licenseeApi.remove(options, auth_token)
        .then((resp) => {
            if(resp) {
                if (resp.success) {
                    // Call getGridData via ref to refresh the grid
                    if (getGridDataRef.current) {
                        getGridDataRef.current();
                    }
                    msgBox_Show(
                        `${resp.count} Licensee items have been deleted...`,
                        undefined,
                        "info"
                    );
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while attempting to delete Licensee data... " +
                        "please retry you last action. If you continue to experience issues, " +
                        "contact customer support.",
                        "Unable to Delete Licensee data...",
                        "warning"
                    );
                }
            }
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [msgBox_Show, getAccessToken]);

    const dlgBox_Close = useCallback((value?: string) => {
        const selRows = selectionModel;
        setDlgBoxOpen(false);

        if(value && value ==="ok") {
            // delete
            deleteSelectedItems(selRows as (string | number)[]);
        }
    }, [selectionModel, deleteSelectedItems]);

    const dlgBox_Show = (message: string, title?: string) => {
        setDlgBoxProps({
            message: message,
            title: title,
        })
        setDlgBoxOpen(true);
    }

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const onGridPageChange = (page: number) => {
        setGridState((prev) => ({
            ...prev,
            page: page
        }));
    };

    const onGridPageSizeChange = (size: number) => {
        setGridState((prev) => ({
            ...prev,
            page_size: size
        }));
    };

    const onGridFilterChange = (model: GridFilterModel) => {
        if(muiGridFilterIsValid(model)) {
            setGridState((prev) => ({
                ...prev,
                filters: model
            }));
        }
    };

    const onGridSortChange = (model: GridSortModel) => {
        setGridState((prev) => ({
            ...prev,
            sorts: model
        }));
    };

    const onClickNew = (e: React.MouseEvent<HTMLButtonElement>) => {
        navigate("/licensee/new");
    };

    const onClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selRows = selectionModel;
        if(selRows.length === 0)
            msgBox_Show(
                "No item selected... select one item only to be edited",
                "Edit Licensee...",
                "warning"
            );
        if(selRows.length > 1)
            msgBox_Show(
                "Multiple items selected... select one item only to be edited",
                "Edit Licensee...",
                "warning"
            );

        if(selRows.length === 1){
            navigate(`/licensee/${selRows[0]}`);
        }
    };

    const onClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgBox_close for processing of dialog result
        dlgBox_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const execSearch = (term: string) => {
        // Modify Grid Filter model
        let model: GridFilterModel = {
            items: [],
            linkOperator: GridLinkOperator.Or
        }
        // Search CompanyName
        if(term.length > 0) {
            model.items.push({id: 1, columnField: "company_name", operatorValue: "contains", value: term});
        }

        setGridState((prev) => ({
            ...prev,
            filters: model
        }));
    };

    const onClickReset = (e: React.MouseEvent<HTMLButtonElement>) => {
        setGridState((prev) => ({
            ...prev,
            filters: {
                items: [],
                linkOperator: GridLinkOperator.Or
            },
            sorts: []
        }));
    };

    const getGridData = useCallback(async () => {
        setIsLoading(true);

        const options: IMuiDataRequest = gridState;
        const auth_token: string = await getAccessToken();

        licenseeApi.get(options, auth_token)
        .then((resp) => {
            if(resp) {
                if (resp.success) {
                    setGridData({
                        rows: resp.data,
                        row_count: resp.count!
                    });
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while attempting to get Licensee data... \n" +
                        "please retry your last action. If you continue to experience issues, " +
                        "please contact customer support.",
                        "Unable to Load Licensee data...",
                        "warning"
                    );
                }
            }
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [gridState, getAccessToken, msgBox_Show]);

    // Store getGridData in ref so it can be called from deleteSelectedItems
    useEffect(() => {
        getGridDataRef.current = getGridData;
    }, [getGridData]);

    // Track previous values to prevent unnecessary API calls
    const prevValuesRef = useRef<string>('');

    useEffect(() => {
        // Create a stable key from the values that matter
        const currentKey = JSON.stringify({
            page: gridState.page,
            page_size: gridState.page_size,
            filters: gridState.filters,
            sorts: gridState.sorts
        });

        // Only call API if values actually changed
        if (prevValuesRef.current !== currentKey) {
            prevValuesRef.current = currentKey;
            getGridData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridState.page, gridState.page_size, gridState.filters, gridState.sorts]);

    const lvGridToolbar = () => {
        const isAdmin = userHasPrivilege([AuthRolesEnum.SystemAdmin]);
        const isLicensee = userHasPrivilege([AuthRolesEnum.Licensee]);
        return (
            <ListViewToolbar
                onClickNew={onClickNew}
                onClickEdit={onClickEdit}
                onClickDelete={onClickDelete}
                onClickReset={onClickReset}
                execSearch={execSearch}
                canView={isAdmin}
                canAdd={isAdmin}
                canUpdate={isAdmin || isLicensee}
                canDelete={isAdmin}
            />
        );
    }

    return (
        <>
            <Box
                sx={{
                    paddingTop: 3,
                }}
            >
                <FormTitle
                    title={"Licensees"}
                    titleIcon={"corporate_fare_rounded_icon"}
                    typeVariant={"h4"}
                />
                    {isAuthenticated ? (
                        <Box sx={{ marginTop: 2 }}>
                            <DaGridContainer
                                sx={{
                                    height: 600,
                                    width: '100%',
                                }}
                            >
                                <DataGrid
                                    components={{
                                        LoadingOverlay: DaGridLoadingOverlay,
                                        Toolbar: lvGridToolbar,
                                    }}
                                    loading={isLoading}
                                    columns={columns}
                                    rows={gridData.rows}
                                    rowCount={gridData.row_count}
                                    getRowId={(row) => row.licensee_id}
                                    filterMode="server"
                                    paginationMode="server"
                                    sortingMode="server"
                                    pagination
                                    page={gridState.page}
                                    pageSize={gridState.page_size}
                                    onFilterModelChange={onGridFilterChange}
                                    onSortModelChange={onGridSortChange}
                                    onPageChange={onGridPageChange}
                                    onPageSizeChange={onGridPageSizeChange}
                                    rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                    headerHeight={DaGridSettings.headerRowHeight}
                                    rowHeight={DaGridSettings.rowHeight}
                                    checkboxSelection
                                    disableSelectionOnClick
                                    selectionModel={selectionModel}
                                    onSelectionModelChange={(newSelection) => {
                                        setSelectionModel(newSelection);
                                    }}
                                />
                            </DaGridContainer>
                        </Box>
                    ) : (
                        <Typography variant="h5">Please Login to use this system...</Typography>
                    )}
            </Box>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
            <DialogBox
                isOpen={dlgBoxOpen}
                onClose={dlgBox_Close}
                {...dlgBoxProps}
            />
        </>
    );
}
